import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * DazeCrew DNF Counter — Liga-Inside-Joke
 * Sitzt fixed unten-rechts. Geschlossen = schmales Pill mit Crash-Emoji + Text.
 * Klick öffnet das Pill, zeigt den globalen Zähler. Jeder Klick auf die Zahl
 * triggert einen atomaren $inc auf dem Backend und animiert die Ziffer.
 * Auto-Sync alle 30s, damit andere Liga-Mitglieder ihre Inkremente sehen.
 */
export default function DnfCounterBadge() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [bumping, setBumping] = useState(false);
  const wrapRef = useRef(null);
  const pendingRef = useRef(0); // optimistic queue offset

  // Initial fetch + periodic sync
  useEffect(() => {
    let mounted = true;
    const refresh = () => {
      api.get("/counters/dnf").then(({ data }) => {
        if (!mounted) return;
        // Server count + lokale optimistische Pendings, damit beim Sync nichts hin-und-her-springt
        setCount((c) => Math.max(c, Number(data?.count || 0) + pendingRef.current));
      }).catch(() => {});
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const increment = async () => {
    pendingRef.current += 1;
    setCount((c) => c + 1);
    setBumping(true);
    setTimeout(() => setBumping(false), 350);
    try {
      const { data } = await api.post("/counters/dnf/increment");
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      setCount(Number(data?.count || 0) + pendingRef.current);
    } catch {
      // Server-Fail: rollback
      pendingRef.current = Math.max(0, pendingRef.current - 1);
      setCount((c) => Math.max(0, c - 1));
    }
  };

  const onPillClick = () => {
    if (!open) setOpen(true);
    else increment();
  };

  return (
    <div
      ref={wrapRef}
      style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999 }}
      data-testid="dnf-counter"
    >
      <button
        type="button"
        onClick={onPillClick}
        title={open ? "Klicken zum Hochzählen · außen klicken zum Schließen" : "DazeCrew DNF Counter öffnen"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 14px",
          background: "#000",
          color: "#fff",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.5)",
          cursor: "pointer",
          fontFamily: "'Inter', sans-serif",
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: 0.2,
          transition: "padding 220ms ease, gap 220ms ease, border-color 220ms ease",
          ...(open ? { padding: "10px 18px", gap: 14, borderColor: "rgba(255,59,48,0.6)" } : {}),
        }}
        data-testid="dnf-counter-button"
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 22,
            height: 22,
            borderRadius: 999,
            background: "linear-gradient(135deg, #FF3B30 0%, #B91C1C 100%)",
            fontSize: 13,
          }}
        >
          {/* Crash / Skull-Glyph für DNF */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 5l14 14" />
            <path d="M19 5L5 19" />
          </svg>
        </span>
        <span>DazeCrew DNF Counter</span>
        <span
          data-testid="dnf-counter-value"
          style={{
            overflow: "hidden",
            maxWidth: open ? 240 : 0,
            opacity: open ? 1 : 0,
            transform: open ? "translateX(0)" : "translateX(-6px)",
            transition: "max-width 280ms ease, opacity 220ms ease 60ms, transform 220ms ease 60ms",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            paddingLeft: open ? 10 : 0,
            borderLeft: open ? "1px solid rgba(255,255,255,0.15)" : "none",
            fontFamily: "'Racing Sans One', 'Inter', sans-serif",
            color: "#FF453A",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              fontFamily: "'Racing Sans One', 'Inter', system-ui, sans-serif",
              fontSize: 22,
              fontStyle: "italic",
              fontWeight: 900,
              color: "#FF453A",
              transform: bumping ? "scale(1.25)" : "scale(1)",
              textShadow: bumping ? "0 0 18px rgba(255,69,58,0.7)" : "0 0 0 rgba(0,0,0,0)",
              transition: "transform 240ms cubic-bezier(.34,1.56,.64,1), text-shadow 280ms ease",
              display: "inline-block",
            }}
          >
            {count.toLocaleString("de-DE")}
          </span>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: 1 }}>
            +1
          </span>
        </span>
      </button>
    </div>
  );
}
